import * as React from 'react';

import {
  ContentCard,
  ContentSection,
  PhotoPreviewDialog,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';

import { useLoadFiles } from './hooks/useLoadFiles';
import {
  OtherPhotoCard,
  PhotoGrid,
  PhotoRequirementTags,
  PhotoWarningBanner,
  RequiredPhotoCard,
} from './LoadPhotos';

import {
  $applyOptimisticUpdate,
  $markOptimisticUpdateError,
  $optimisticPhotoUpdates,
  $removeOptimisticUpdate,
} from '~/modules/trade/_state/files.state';
import {
  $loadPhotos,
  $loadPhotosWithOptimistic,
  $photoRequirementsData,
  $photoUploadStatus,
} from '~/modules/trade/_state/loadDetail.state';
import { $photoRequirements } from '~/modules/trade/_state/loadTabPhotos.state';
import { usePhotoPreview } from '~/modules/trade/hooks/usePhotoPreview';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { LoadRecord } from '~/modules/trade/types/load';
import { FilesTab } from '~/shared/components/files';
import { useFileDownload } from '~/shared/hooks/useFileDownload';
import { GenericFile, UploadErrorActions } from '~/shared/types/file';

type LoadTabPhotosProp = {
  load?: LoadRecord | null;
};

export const LoadTabPhotos: React.FC<LoadTabPhotosProp> = ({ load }) => {
  // ═══════════════════════════════════════════════════════════════════════════
  // HOOKS & STATE
  // ═══════════════════════════════════════════════════════════════════════════
  const t = useTranslations();
  const toast = useToast();

  const { downloadFile, isGeneratingDownloadUrl } = useFileDownload();
  const loadPhotoMutations = useLoadFiles();

  // Use optimistic photos for display, but keep reference to base $loadPhotos for refetch
  const loadPhotosResult = useAtomValue($loadPhotosWithOptimistic);
  const baseLoadPhotosResult = useAtomValue($loadPhotos);
  const photoRequirementsData = useAtomValue($photoRequirementsData);
  const photoUploadStatus = useAtomValue($photoUploadStatus);
  const optimisticUpdates = useAtomValue($optimisticPhotoUpdates);

  const setPhotoRequirements = useSetAtom($photoRequirements);
  const applyOptimisticUpdate = useSetAtom($applyOptimisticUpdate);
  const markOptimisticUpdateError = useSetAtom($markOptimisticUpdateError);
  const removeOptimisticUpdate = useSetAtom($removeOptimisticUpdate);

  const handleAllUploadsComplete = React.useCallback(() => {
    baseLoadPhotosResult.refetch();
  }, [baseLoadPhotosResult]);

  const hasBooking = !!load?.bookingId;

  // ═══════════════════════════════════════════════════════════════════════════
  // DERIVED DATA
  // ═══════════════════════════════════════════════════════════════════════════
  const photoFiles = React.useMemo<GenericFile[]>(() => {
    // Use data if available, even while loading (to prevent flicker during refetch)
    // Only return empty array if there's truly no data
    if (loadPhotosResult?.data) {
      return loadPhotosResult.data.map((photo) => ({
        id: photo.id,
        fileName: photo.fileName,
        fileSize: photo.fileSize,
        mimeType: photo.mimeType,
        status: photo.status,
        downloadUrl: photo.url,
        entityType: 'Load',
        entityId: load?.id,
        metadata: photo.metadata || {},
        createdAt: photo.createdAt?.toString(),
        updatedAt: photo.updatedAt?.toString(),
        // Include optimistic state for UI rendering
        _optimistic: photo._optimistic,
      }));
    }
    return [];
  }, [loadPhotosResult?.data, load?.id]);

  const requiredPhotos = React.useMemo(
    () =>
      photoFiles.filter(
        (file) =>
          file.metadata?.isPhotoRequirement ||
          file.metadata?.photoRequirementId != null,
      ),
    [photoFiles],
  );

  const otherPhotos = React.useMemo(
    () => photoFiles.filter((file) => !file.metadata?.isPhotoRequirement),
    [photoFiles],
  );

  const {
    previewOpen,
    currentPhotoIndex,
    previewPhotos,
    handlePhotoView,
    closePreview,
    navigatePreview,
  } = usePhotoPreview(photoFiles);

  // ═══════════════════════════════════════════════════════════════════════════
  // MUTATION REFS (kept current via effect)
  // ═══════════════════════════════════════════════════════════════════════════
  const updateFileMetadataRef = React.useRef(
    loadPhotoMutations.updateFileMetadata,
  );

  const deleteFileRef = React.useRef(loadPhotoMutations.deleteFile);

  // ═══════════════════════════════════════════════════════════════════════════
  // PHOTO OPERATIONS (Delete, Update, Notes)
  // ═══════════════════════════════════════════════════════════════════════════
  const handleDeletePhoto = React.useCallback(
    async (photoId: string) => {
      const currentPhoto = photoFiles.find((p) => p.id === photoId);
      if (!currentPhoto) return;

      // Apply optimistic delete immediately (photo disappears)
      applyOptimisticUpdate({
        id: photoId,
        type: 'delete',
        previousData: currentPhoto as Record<string, unknown>,
      });

      try {
        await deleteFileRef.current({ fileId: photoId });
        // DON'T remove optimistic state here - the mutation's onSuccess will trigger a refetch
        // The optimistic state will be cleaned up once the photo is confirmed gone from server
        // This prevents flickering where the photo briefly reappears during refetch
        toast.success(t.loadDetail.photos.photoDeleted);
      } catch (error) {
        console.error('Failed to delete photo:', error);
        // Mark as error - photo reappears with error indicator
        markOptimisticUpdateError({
          id: photoId,
          error: t.loadDetail.photos.photoDeleteFailed,
        });
        toast.error(t.loadDetail.photos.photoDeleteFailed);
      }
    },
    [photoFiles, applyOptimisticUpdate, markOptimisticUpdateError, t, toast],
  );

  const handlePhotoNotesChange = React.useCallback(
    async (photoId: string, notes: string) => {
      try {
        await updateFileMetadataRef.current?.({
          fileId: photoId,
          notes: notes || undefined,
        });
      } catch (error) {
        console.error('Failed to update photo notes:', error);
      }
    },
    [],
  );

  const handlePhotoRequirementChange = React.useCallback(
    async (photoId: string, requirementId?: string) => {
      const currentPhoto = photoFiles.find((p) => p.id === photoId);
      if (!currentPhoto) return;

      // Apply optimistic update immediately
      // Keep isPhotoRequirement true - photos in the Required Photos section stay there
      // even when their specific requirement is cleared
      applyOptimisticUpdate({
        id: photoId,
        type: 'update',
        data: {
          metadata: {
            ...currentPhoto.metadata,
            photoRequirementId: requirementId ?? null,
            isPhotoRequirement: true,
          },
        },
        previousData: currentPhoto as Record<string, unknown>,
      });

      try {
        await updateFileMetadataRef.current?.({
          fileId: photoId,
          notes: currentPhoto?.metadata?.notes || undefined,
          photoRequirementId: requirementId ?? null,
        });
        // DON'T remove optimistic state here - mutation's onSuccess will trigger refetch
        // The optimistic state will be cleaned up once server data confirms the change
        toast.success(t.loadDetail.photos.photoRequirementUpdated);
      } catch (error) {
        console.error('Failed to change photo requirement:', error);
        // Mark as error - keep showing with error state
        markOptimisticUpdateError({
          id: photoId,
          error: t.loadDetail.photos.photoRequirementUpdateFailed,
        });
        toast.error(t.loadDetail.photos.photoRequirementUpdateFailed);
      }
    },
    [photoFiles, applyOptimisticUpdate, markOptimisticUpdateError, toast, t],
  );

  // ═══════════════════════════════════════════════════════════════════════════
  // ERROR RECOVERY
  // ═══════════════════════════════════════════════════════════════════════════
  const handleRetryOptimisticUpdate = React.useCallback(
    async (photoId: string) => {
      const optimisticUpdate = optimisticUpdates.get(photoId);
      if (!optimisticUpdate || optimisticUpdate.status !== 'error') return;

      if (optimisticUpdate.type === 'update') {
        // Re-apply as pending
        applyOptimisticUpdate({
          id: photoId,
          type: 'update',
          data: optimisticUpdate.data,
          previousData: optimisticUpdate.previousData,
        });

        // Retry the update mutation
        try {
          const metadata = optimisticUpdate.data?.metadata as
            | Record<string, unknown>
            | undefined;
          await updateFileMetadataRef.current?.({
            fileId: photoId,
            notes: (metadata?.notes as string) || undefined,
            photoRequirementId:
              (metadata?.photoRequirementId as string) ?? null,
          });
          removeOptimisticUpdate(photoId);
          toast.success(t.loadDetail.photos.photoRequirementUpdated);
        } catch (_error) {
          markOptimisticUpdateError({
            id: photoId,
            error: t.loadDetail.photos.photoRequirementUpdateFailed,
          });
          toast.error(t.loadDetail.photos.photoRequirementUpdateFailed);
        }
      } else if (optimisticUpdate.type === 'delete') {
        // Re-apply as pending
        applyOptimisticUpdate({
          id: photoId,
          type: 'delete',
          previousData: optimisticUpdate.previousData,
        });

        // Retry the delete mutation
        try {
          await deleteFileRef.current({ fileId: photoId });
          // Don't remove optimistic state here - let the cleanup effect handle it
          // after refetch confirms the photo is gone from server
          toast.success(t.loadDetail.photos.photoDeleted);
        } catch (_error) {
          markOptimisticUpdateError({
            id: photoId,
            error: t.loadDetail.photos.photoDeleteFailed,
          });
          toast.error(t.loadDetail.photos.photoDeleteFailed);
        }
      }
    },
    [
      optimisticUpdates,
      applyOptimisticUpdate,
      removeOptimisticUpdate,
      markOptimisticUpdateError,
      toast,
      t,
    ],
  );

  // Dismiss error and revert to server state
  const handleDismissOptimisticError = React.useCallback(
    (photoId: string) => {
      removeOptimisticUpdate(photoId);
    },
    [removeOptimisticUpdate],
  );

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER HELPERS
  // ═══════════════════════════════════════════════════════════════════════════
  const renderRequiredFiles = React.useCallback(
    (files: GenericFile[], uploadErrorActions: UploadErrorActions) => (
      <PhotoGrid>
        {files.map((file) => {
          // Determine if this is an upload error or optimistic update error
          const hasUploadError = file.uploadState === 'error';
          const hasOptimisticError = file._optimistic?.status === 'error';

          // Use appropriate handlers based on error type
          const handleRetry = hasUploadError
            ? () => uploadErrorActions.retryUpload(file.id)
            : hasOptimisticError
              ? () => handleRetryOptimisticUpdate(file.id)
              : undefined;

          const handleDismiss = hasUploadError
            ? () => uploadErrorActions.dismissError(file.id)
            : hasOptimisticError
              ? () => handleDismissOptimisticError(file.id)
              : undefined;

          return (
            <RequiredPhotoCard
              key={file.id}
              file={file}
              onView={() => handlePhotoView(file.id)}
              onDownload={() => downloadFile(file)}
              onDelete={() => handleDeletePhoto(file.id)}
              onRequirementChange={(requirementId) =>
                handlePhotoRequirementChange(file.id, requirementId)
              }
              onRetry={handleRetry}
              onDismissError={handleDismiss}
              isDownloading={isGeneratingDownloadUrl}
            />
          );
        })}
      </PhotoGrid>
    ),
    [
      handlePhotoRequirementChange,
      downloadFile,
      isGeneratingDownloadUrl,
      handleDeletePhoto,
      handleRetryOptimisticUpdate,
      handleDismissOptimisticError,
      handlePhotoView,
    ],
  );

  const renderOtherFiles = React.useCallback(
    (files: GenericFile[], uploadErrorActions: UploadErrorActions) => (
      <PhotoGrid>
        {files.map((file) => {
          // Determine if this is an upload error or optimistic update error
          const hasUploadError = file.uploadState === 'error';
          const hasOptimisticError = file._optimistic?.status === 'error';

          // Use appropriate handlers based on error type
          const handleRetry = hasUploadError
            ? () => uploadErrorActions.retryUpload(file.id)
            : hasOptimisticError
              ? () => handleRetryOptimisticUpdate(file.id)
              : undefined;

          const handleDismiss = hasUploadError
            ? () => uploadErrorActions.dismissError(file.id)
            : hasOptimisticError
              ? () => handleDismissOptimisticError(file.id)
              : undefined;

          return (
            <OtherPhotoCard
              key={file.id}
              file={file}
              onView={() => handlePhotoView(file.id)}
              onDownload={() => downloadFile(file)}
              onDelete={() => handleDeletePhoto(file.id)}
              onNotesChange={(notes) => handlePhotoNotesChange(file.id, notes)}
              onRetry={handleRetry}
              onDismissError={handleDismiss}
              isDownloading={isGeneratingDownloadUrl}
            />
          );
        })}
      </PhotoGrid>
    ),
    [
      downloadFile,
      isGeneratingDownloadUrl,
      handleDeletePhoto,
      handlePhotoNotesChange,
      handleRetryOptimisticUpdate,
      handleDismissOptimisticError,
      handlePhotoView,
    ],
  );

  const renderDropZoneText = React.useMemo(
    () => ({
      primary: (
        <>
          {t.loadDetail.photos.dropZone.primaryClick}{' '}
          <span className="font-semibold text-primary">
            {t.loadDetail.photos.dropZone.primaryHere}
          </span>{' '}
          {t.loadDetail.photos.dropZone.primaryDrag}{' '}
        </>
      ),
      secondary: t.loadDetail.photos.dropZone.secondary,
    }),
    [t.loadDetail.photos.dropZone],
  );

  // ═══════════════════════════════════════════════════════════════════════════
  // EFFECTS
  // ═══════════════════════════════════════════════════════════════════════════

  /*
   * Keep mutation ref current
   */
  React.useEffect(() => {
    updateFileMetadataRef.current = loadPhotoMutations.updateFileMetadata;
    deleteFileRef.current = loadPhotoMutations.deleteFile;
  }, [loadPhotoMutations.updateFileMetadata, loadPhotoMutations.deleteFile]);

  /*
   * Sync photo requirements data
   */
  React.useEffect(() => {
    setPhotoRequirements(photoRequirementsData);
  }, [photoRequirementsData, setPhotoRequirements]);

  /*
   * Create a stable fingerprint of optimistic updates to avoid unnecessary effect runs.
   * The Map reference changes on every Jotai update, but we only need to re-run cleanup
   * when the actual content (IDs and their statuses) changes.
   */
  const optimisticUpdatesFingerprint = React.useMemo(() => {
    const entries: string[] = [];
    optimisticUpdates.forEach((update, id) => {
      entries.push(`${id}:${update.type}:${update.status}`);
    });
    return entries.sort().join('|');
  }, [optimisticUpdates]);

  /*
   * Clean up stale optimistic delete entries once server confirms photo is gone.
   * This prevents memory leaks and ensures optimistic state doesn't accumulate.
   *
   * Flow:
   * 1. User deletes photo → optimistic delete applied (photo hidden immediately)
   * 2. Server mutation succeeds → triggers data refetch
   * 3. This effect runs on new server data
   * 4. Photo no longer in server response → cleanup optimistic state
   *
   * Note: We use optimisticUpdatesFingerprint instead of optimisticUpdates directly
   * because the Map reference changes on every Jotai update, but we only need to
   * re-run when content actually changes. The eslint-disable is intentional.
   */
  React.useEffect(() => {
    if (!baseLoadPhotosResult.data) return;

    const serverPhotoIds = new Set(baseLoadPhotosResult.data.map((p) => p.id));

    // Find optimistic deletes where photo is no longer in server data
    optimisticUpdates.forEach((update, photoId) => {
      if (update.type === 'delete' && !serverPhotoIds.has(photoId)) {
        // Photo confirmed deleted from server, clean up optimistic state
        removeOptimisticUpdate(photoId);
      }
    });
  }, [
    baseLoadPhotosResult.data,
    optimisticUpdatesFingerprint,
    removeOptimisticUpdate,
  ]);

  /*
   * Clean up stale optimistic update entries once server confirms the photo requirement change.
   * This prevents flickering by keeping optimistic state until server data matches.
   *
   * Flow:
   * 1. User changes photo requirement → optimistic update applied (UI updates immediately)
   * 2. Server mutation succeeds → triggers data refetch
   * 3. This effect runs on new server data
   * 4. Server photo has matching photoRequirementId → cleanup optimistic state
   *
   * Why we compare values: Unlike deletes, updates need to verify the server actually
   * has the new value before removing optimistic state. This prevents a brief flicker
   * where the old value would show between optimistic removal and server data arrival.
   *
   * Note: We use optimisticUpdatesFingerprint instead of optimisticUpdates directly
   * because the Map reference changes on every Jotai update, but we only need to
   * re-run when content actually changes. The eslint-disable is intentional.
   */
  React.useEffect(() => {
    if (!baseLoadPhotosResult.data) return;

    const serverPhotosById = new Map(
      baseLoadPhotosResult.data.map((p) => [p.id, p]),
    );

    // Find optimistic updates where server data already reflects the change
    optimisticUpdates.forEach((update, photoId) => {
      if (update.type === 'update' && update.status === 'pending') {
        const serverPhoto = serverPhotosById.get(photoId);
        if (serverPhoto) {
          // Check if server has the expected photoRequirementId
          const expectedRequirementId = (
            update.data?.metadata as Record<string, unknown> | undefined
          )?.photoRequirementId;
          const serverRequirementId = serverPhoto.metadata?.photoRequirementId;

          if (expectedRequirementId === serverRequirementId) {
            // Server confirmed the change, clean up optimistic state
            removeOptimisticUpdate(photoId);
          }
        }
      }
    });
  }, [
    baseLoadPhotosResult.data,
    optimisticUpdatesFingerprint,
    removeOptimisticUpdate,
  ]);

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <div className="@LoadTabPhotos relative flex flex-col gap-5 p-5">
      <ContentCard className="overflow-visible pt-0">
        <PhotoWarningBanner
          hasBooking={hasBooking}
          photoUploadStatus={photoUploadStatus}
        />

        <ContentCard className="border-none bg-none px-0">
          {hasBooking && (
            <ContentSection
              title={t.loadDetail.photos.requiredPhotos}
              required
              titleClassName="font-bold text-neutral"
            >
              <FilesTab
                entityId={load.id}
                files={requiredPhotos}
                mutations={loadPhotoMutations}
                onAllUploadsComplete={handleAllUploadsComplete}
                required
                mimeType="image/*"
                defaultMetadata={{ isPhotoRequirement: true }}
                renderItemsHeader={(files) => (
                  <PhotoRequirementTags
                    photoRequirements={photoRequirementsData}
                    files={files}
                  />
                )}
                renderItems={(files, _defaultRenderer, uploadErrorActions) =>
                  renderRequiredFiles(files, uploadErrorActions)
                }
                dropZoneTheme="compact"
                dropZoneText={renderDropZoneText}
              />
            </ContentSection>
          )}

          <ContentSection
            title={
              hasBooking
                ? t.loadDetail.photos.otherPhotos
                : t.loadDetail.photos.title
            }
            titleClassName="font-bold text-neutral"
          >
            <FilesTab
              entityId={load?.id}
              files={otherPhotos}
              mutations={loadPhotoMutations}
              onAllUploadsComplete={handleAllUploadsComplete}
              mimeType="image/*"
              renderItems={(files, _defaultRenderer, uploadErrorActions) =>
                renderOtherFiles(files, uploadErrorActions)
              }
              dropZoneTheme="compact"
              dropZoneText={renderDropZoneText}
            />
          </ContentSection>
        </ContentCard>
      </ContentCard>
      <PhotoPreviewDialog
        albumName={t.loadDetail.photos.title}
        photos={previewPhotos}
        currentIndex={currentPhotoIndex}
        isOpen={previewOpen}
        onClose={closePreview}
        onNavigate={navigatePreview}
      />
    </div>
  );
};
